import type {
  MergeCountsDto,
  MergeSection,
} from '../api/mergeResolution.types';

import type { MergeDecisionDto } from '../api/mergeResolution.types';

import type { CellKey, LoadedPage } from './types';
import { cellKey } from './cellKey';

export function tabLabel(
  section: MergeSection,
  input: { name: string; groupName?: string },
  c: {
    autoResolved: number;
    remaining: number;
    total: number;
    downstream?: number;
  }
): string {
  const prefix = input.groupName ? `${input.groupName}: ` : '';

  if (section === 'auto_resolved') {
    return `${prefix}${input.name} (${c.autoResolved})`;
  }

  return `${prefix}${input.name} (${c.remaining}/${c.total})`;
}

// The counts a header line reads: the whole merge's, or one input's.
export type HeaderCounts = Pick<
  MergeCountsDto,
  'TotalConflicts' | 'Resolved' | 'Remaining' | 'AutoResolved' | 'Overrides'
>;

export function sectionHeader(
  section: MergeSection,
  counts: HeaderCounts
): string {
  if (section === 'auto_resolved') {
    return `AutoResolved Conflicts: ${counts.AutoResolved} | Overrides: ${counts.Overrides}`;
  }

  return `Conflicts to Resolve: ${counts.TotalConflicts} | Resolved: ${counts.Resolved} | Remaining: ${counts.Remaining}`;
}

// Accessible name of a section's page buttons. Worded after the section
// accordion's aria-label ("Rows needing a decision" / "Auto resolved rows"),
// so the two pagers, both mounted at once, are told apart.
export function sectionPagerLabel(section: MergeSection): string {
  return section === 'auto_resolved'
    ? 'Pages of auto resolved rows'
    : 'Pages of rows needing a decision';
}

export function progress(counts: HeaderCounts): number {
  return counts.TotalConflicts === 0
    ? 0
    : counts.Resolved / counts.TotalConflicts;
}

// `decisions` and `staleResolved` only matter to the page-derived fallback
// below: a CONFLICT is resolved if it has a local decision, or if the loaded
// row carries a `Resolved` that a removed decision has not made stale.
export function inputTabs(
  counts: MergeCountsDto,
  pages: Partial<Record<MergeSection, LoadedPage>>,
  section: MergeSection,
  decisions: Record<CellKey, MergeDecisionDto> = {},
  staleResolved: Record<CellKey, true> = {},
  changedUnderneathOnly = false
): { key: string; label: string }[] {
  // Use ByInput if present
  if (counts.ByInput) {
    // An input is listed when it takes part in the merge at all, in either
    // section, rather than only when it is relevant to the section currently
    // expanded.
    //
    // This reverses the original Task 15 rule, which required
    // `TotalConflicts > 0` for needs_decision and `AutoResolved > 0` for
    // auto_resolved. The consequence was a rail that changed shape as sections
    // were expanded, and - for a submission with nothing to resolve, which is
    // a case the screen exists to handle (D77) - an empty rail with nothing to
    // click, even though every input had auto-resolved rows to look at. The
    // label is still section-specific, so the numbers beside each input still
    // describe the section on screen.
    //
    // With "Show only these" on, an input is listed only if it has conflicts
    // and some of the values that moved underneath the user are on it, so an
    // input with nothing but auto-resolved rows is left out.
    const filtered = counts.ByInput.filter((item) =>
      changedUnderneathOnly
        ? item.TotalConflicts > 0 && item.ChangedUnderneath > 0
        : item.TotalConflicts > 0 || item.AutoResolved > 0
    );

    return filtered.map((item) => ({
      key: item.Input_Name,
      label: tabLabel(
        section,
        { name: item.Input_Name },
        {
          autoResolved: item.AutoResolved,
          remaining: item.Remaining,
          total: item.TotalConflicts,
        }
      ),
    }));
  }

  // Page-derived and display-only fallback
  const tabMap = new Map<
    string,
    { autoResolved: number; remaining: number; total: number }
  >();

  Object.values(pages).forEach((page) => {
    if (!page) return;
    page.rows.forEach((row) => {
      const tabName = row.Tab_Name ?? 'Untitled';
      if (!tabMap.has(tabName)) {
        tabMap.set(tabName, { autoResolved: 0, remaining: 0, total: 0 });
      }
      const stats = tabMap.get(tabName)!;

      row.Periods.forEach((period) => {
        if (period.Class === 'CONFLICT') {
          stats.total += 1;
          const key = cellKey(row.Input_RowID, period.DateID);
          const resolved =
            key in decisions ||
            (period.Resolved !== undefined && !(key in staleResolved));
          if (!resolved) {
            stats.remaining += 1;
          }
        } else if (period.Class !== 'UNCHANGED') {
          stats.autoResolved += 1;
        }
      });
    });
  });

  // Same rule as the ByInput path above: listed if it takes part at all.
  const filtered = Array.from(tabMap.entries()).filter(
    ([, stats]) => stats.total > 0 || stats.autoResolved > 0
  );

  return filtered.map(([tabName, stats]) => ({
    key: tabName,
    label: tabLabel(
      section,
      { name: tabName },
      {
        autoResolved: stats.autoResolved,
        remaining: stats.remaining,
        total: stats.total,
      }
    ),
  }));
}
